import type { Language } from '@expenditure/core';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '../components/Icon';
import { Logo } from '../components/Logo';
import { Segmented } from '../components/ui';
import { setLanguage } from '../lib/i18n';
import { telegramLinkFor } from '../lib/links';

/**
 * The web address opened in a normal browser: what the app is, and a way
 * into it in Telegram (where it signs people in).
 */
export function LandingPage() {
  const { t, i18n } = useTranslation();
  const openLink =
    telegramLinkFor('/', {
      botUsername: import.meta.env.VITE_BOT_USERNAME as string | undefined,
      miniAppLink: import.meta.env.VITE_MINI_APP_LINK as string | undefined,
    }) ?? undefined;

  const features = [
    { icon: 'camera', title: t('landing.scanTitle'), body: t('landing.scanBody') },
    { icon: 'divide', title: t('landing.splitTitle'), body: t('landing.splitBody') },
    { icon: 'qr', title: t('landing.khqrTitle'), body: t('landing.khqrBody') },
    { icon: 'bell', title: t('landing.remindTitle'), body: t('landing.remindBody') },
  ] satisfies Array<{ icon: IconName; title: string; body: string }>;

  const steps = [t('landing.step1'), t('landing.step2'), t('landing.step3')];
  const screens = [
    { src: '/landing/dashboard.webp', alt: t('landing.shotHome') },
    { src: '/landing/add-expense.webp', alt: t('landing.shotAdd') },
    { src: '/landing/settle-up.webp', alt: t('landing.shotSettle') },
  ];

  const OpenButton = ({ className = '' }: { className?: string }) =>
    openLink ? (
      <a className={`btn btn--primary landing-cta ${className}`} href={openLink}>
        <Icon name="send" size={18} />
        {t('landing.open')}
      </a>
    ) : null;

  return (
    <div className="landing">
      <header className="landing-hero">
        <nav className="landing-nav">
          <span className="landing-brand">
            <Logo size={36} />
            <span>{t('app.name')}</span>
          </span>
          <Segmented<Language>
            value={i18n.language === 'km' ? 'km' : 'en'}
            onChange={setLanguage}
            label={t('welcome.language')}
            options={[
              { value: 'en', label: 'EN' },
              { value: 'km', label: 'ខ្មែរ' },
            ]}
          />
        </nav>
        <div className="landing-hero-body">
          <div className="landing-hero-text">
            <h1>{t('landing.title')}</h1>
            <p>{t('landing.subtitle')}</p>
            <OpenButton />
            <div className="landing-facts">
              <span>{t('landing.factFree')}</span>
              <span>{t('landing.factLanguages')}</span>
              <span>{t('landing.factCurrencies')}</span>
            </div>
          </div>
          <img className="landing-phone" src="/landing/dashboard.webp" alt={t('landing.shotHome')} width={390} height={844} />
        </div>
      </header>

      <main className="landing-main">
        <section className="landing-features" aria-label={t('landing.featuresLabel')}>
          {features.map((f) => (
            <div className="card landing-feature" key={f.title}>
              <span className="icon-tile icon-tile--ink" aria-hidden>
                <Icon name={f.icon} size={20} />
              </span>
              <h2>{f.title}</h2>
              <p className="muted">{f.body}</p>
            </div>
          ))}
        </section>

        <section className="landing-how">
          <h2>{t('landing.howTitle')}</h2>
          <ol>
            {steps.map((step, i) => (
              <li key={step}>
                <span className="landing-step num" aria-hidden>
                  {i + 1}
                </span>
                {step}
              </li>
            ))}
          </ol>
        </section>

        <section className="landing-screens" aria-label={t('landing.screensLabel')}>
          {screens.map((s) => (
            <img key={s.src} src={s.src} alt={s.alt} width={390} height={844} loading="lazy" />
          ))}
        </section>

        <section className="landing-final">
          <h2>{t('landing.finalTitle')}</h2>
          <OpenButton />
        </section>
      </main>

      <footer className="landing-footer">
        <a href="/privacy">{t('profile.privacy')}</a>
        <a href="/terms">{t('profile.terms')}</a>
        <span>© 2026 {t('app.name')}</span>
      </footer>
    </div>
  );
}
